import { useEffect, useMemo, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { backdrop, panelCollapse, tap } from "../motion";
import { IconHandover, IconX } from "./icons";
import { Select } from "./Select";
import { useHandoverDraft, useSaveHandoverDraft, useWrapUp } from "../hooks/useEndOfDay";
import { hkToday } from "../lib/dates";
import { useAgenda } from "../hooks/useAgenda";
import { useHomes } from "../hooks/useSettings";

// End-of-day wrap-up — one closeout panel: completed to-dos, carry-over,
// crossed deadlines, and an editable draft 交更 message to copy into WhatsApp.
// §1 and the OT-side §3 numbers are computed from app data; §2, the PT-side
// §3 values and the custom §4 carry over from the home's last saved draft
// (copying saves that version). The day note itself lives on the Dashboard
// page (ADR-0009) — no notes field here.
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <p className="agenda-subhead">{title}</p>
      {children}
    </section>
  );
}

function DrawerBody({ date }: { date: string }) {
  const wrap = useWrapUp(date);
  const { data: agenda } = useAgenda();
  const { data: allHomes } = useHomes();
  const [homeId, setHomeId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [draftText, setDraftText] = useState<string | null>(null); // user edits override the generated draft
  const [saveFailed, setSaveFailed] = useState(false);
  const saveDraft = useSaveHandoverDraft();

  // Draft home: first of today's shift homes (fallback: the workspace list).
  const shiftHomeIds = useMemo(() => (agenda?.shifts ?? []).map((s) => s.homeId), [agenda]);
  const draftHomeId = homeId ?? shiftHomeIds[0] ?? allHomes?.[0]?.id ?? null;
  const draft = useHandoverDraft(draftHomeId);

  // Switching homes must not leak one home's edited text into another's draft.
  useEffect(() => {
    setDraftText(null);
  }, [draftHomeId]);

  const w = wrap.data;

  // Keep in sync with handover-generate.ts HANDOVER_REMINDER — shown in the
  // draft next to carried-over values, stripped before save/copy so it never
  // reaches WhatsApp.
  const REMINDER = "（如有更改請更新）";

  async function copyDraft() {
    if (!draft.data || !draftHomeId) return;
    const raw = draftText ?? draft.data.text;
    const text = raw.replaceAll(REMINDER, "").split("\n").map((l) => l.trimEnd()).join("\n");
    try {
      await saveDraft.mutateAsync({ homeId: draftHomeId, text });
      setSaveFailed(false);
    } catch {
      // The copy is the user's goal; a failed draft-save must not block it.
      setSaveFailed(true);
    }
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const draftOptions = (allHomes ?? []).map((h) => ({ value: h.id, label: shiftHomeIds.includes(h.id) ? `${h.name} · shift today` : h.name }));

  return (
    <div className="modal-body">
      {wrap.isLoading ? <p className="muted">Loading…</p> : null}
      {wrap.isError ? <p className="error">Could not load today's wrap-up. Try again.</p> : null}
      {w ? (
        <>
          <Section title={`Completed today (${w.completedTodos.length})`}>
            {w.completedTodos.length === 0 ? (
              <p className="muted">Nothing completed yet.</p>
            ) : (
              <ul className="mini-list">
                {w.completedTodos.map((todo) => <li key={todo.id} className="mini-row"><p className="mini-title">{todo.title}</p></li>)}
              </ul>
            )}
          </Section>

          <Section title={`Carry over (${w.carryOver.length})`}>
            {w.carryOver.length === 0 ? (
              <p className="muted">Nothing left open past its due date.</p>
            ) : (
              <ul className="mini-list">
                {w.carryOver.map((todo) => (
                  <li key={todo.id} className="mini-row">
                    <p className="mini-title">{todo.title}</p>
                    <span className="chip chip-warn">due {todo.dueDate}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title={`Deadlines crossed (${w.crossed.length})`}>
            {w.crossed.length === 0 ? (
              <p className="muted">No ICP deadline was left unactioned at its due date.</p>
            ) : (
              <ul className="mini-list">
                {w.crossed.map((r) => (
                  <li key={r.id} className="mini-row">
                    <p className="mini-title">{r.name} {r.bed ? <span className="muted">· {r.bed}</span> : null}</p>
                    <span className="chip chip-danger">ICP {r.nextIcpDate}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Draft 交更 message">
            {draftOptions.length === 0 ? (
              <p className="muted">No homes configured.</p>
            ) : (
              <>
                <Select id="eod-home" value={draftHomeId ?? ""} onChange={(v) => setHomeId(v)} placeholder="Choose a home…" options={draftOptions} />
                {draft.isLoading ? <p className="muted">Generating…</p> : null}
                {draft.isError ? <p className="error">Could not generate the draft. Try again.</p> : null}
                {draft.data ? (
                  <>
                    <textarea value={draftText ?? draft.data.text} onChange={(e) => setDraftText(e.target.value)} rows={14} aria-label="Draft handover message" />
                    {saveFailed ? <p className="error">Copied, but this version could not be remembered for next time.</p> : null}
                    <p className="muted">§1 and the OT-side §3 numbers come from app data. §2, the PT-side values and §4 carry over from your last sent draft — edit here, then Copy (Copy also saves this version for next time).</p>
                    <motion.button type="button" className="btn btn-secondary" {...tap} onClick={copyDraft}>
                      <IconHandover /> {copied ? "Copied!" : "Copy for WhatsApp"}
                    </motion.button>
                  </>
                ) : null}
              </>
            )}
          </Section>
        </>
      ) : null}
    </div>
  );
}

export function EndOfDayDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const date = hkToday();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div className="palette-backdrop" role="dialog" aria-modal="true" aria-label="End of day" onClick={onClose} variants={backdrop} initial="hidden" animate="show" exit="exit">
          <motion.div className="modal" onClick={(e) => e.stopPropagation()} variants={panelCollapse} initial="hidden" animate="show" exit="exit">
            <div className="modal-head">
              <h2>End of day · {date}</h2>
              <button type="button" className="btn btn-icon btn-ghost" aria-label="Close" onClick={onClose}><IconX /></button>
            </div>
            <DrawerBody date={date} />
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
